import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Table, Tooltip, Spin } from 'antd';
import { toast } from 'react-toastify';

import { BiExport } from 'react-icons/bi';
import { FaPlus, FaSearch, FaEye, FaPen } from 'react-icons/fa';
import { FaBoxArchive, FaBoxOpen } from "react-icons/fa6";
import { FiFilter } from 'react-icons/fi';
import { HiOutlineRefresh } from 'react-icons/hi';

import '../../../css/UserAdmin/Global.css';
import 'react-toastify/dist/ReactToastify.css';

import Footer from '../Footer';
import Header from '../NewHeader';
import SideNav from '../SideNav';

// API URL should be defined in your environment variables
const API_URL = process.env.REACT_APP_API_URL || '';

// Updated Filter Options based on the new TuitionDetails schema
const categoryFilterOptions = [
    { text: 'Public School', value: 'Public School' },
    { text: 'Private (with PEAC)', value: 'Private with PEAC Voucher' },
    { text: 'Private (no Voucher)', value: 'Private without Voucher' },
];

const semesterFilterOptions = [
    { text: '1st Semester', value: '1st Semester' },
    { text: '2nd Semester', value: '2nd Semester' },
];

const paymentTypeFilterOptions = [
    { text: 'Full Payment', value: 'Full Payment' },
    { text: 'Installment', value: 'Installment' },
];

// Helper to format currency
const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(Number(value))) {
        return 'N/A';
    }
    return `₱ ${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const ManageTuitionDetails = () => {
    const navigate = useNavigate();
    const [isSideNavOpen, setIsSideNavOpen] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);

    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [tableFilters, setTableFilters] = useState({});
    const [sorter, setSorter] = useState({});
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    
    const toggleSideNav = useCallback(() => setIsSideNavOpen(prev => !prev), []);

    useEffect(() => {
        const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Renamed fetchFees to fetchTuitionDetails
    const fetchTuitionDetails = useCallback(async () => {
        setLoading(true);
        try {
            if (!API_URL) {
                toast.error('API URL is not configured.');
                setLoading(false);
                return;
            }

            const params = new URLSearchParams({
                page: String(pagination.current),
                limit: String(pagination.pageSize),
            });

            if (searchTerm) params.append('search', searchTerm);
            if (sorter.field) {
                // Adjust for nested fields if sorting is needed (e.g., 'fees.finalTotal')
                const sortField = Array.isArray(sorter.field) ? sorter.field.join('.') : sorter.field;
                params.append('sortBy', sortField);
                params.append('sortOrder', sorter.order === 'ascend' ? 'asc' : 'desc');
            }

            Object.entries(tableFilters).forEach(([key, value]) => {
                if (value && value.length > 0) {
                    if (Array.isArray(value)) {
                        value.forEach(v => params.append(key, String(v)));
                    } else {
                        params.append(key, String(value));
                    }
                }
            });

            // *** IMPORTANT: Updated API endpoint for admin fetching ***
            const response = await fetch(`${API_URL}/api/admin/tuition-details?${params.toString()}`);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: `Server error: ${response.status}` }));
                throw new Error(errorData.message);
            }

            const result = await response.json();
            setDataSource(result.data.map(item => ({ ...item, key: item._id })) || []);
            setPagination(prev => ({ 
                ...prev, 
                total: result.pagination?.totalItems || 0,
                current: result.pagination?.currentPage || 1,
                pageSize: result.pagination?.pageSize || 10,
            }));

            if (!result.data?.length && (searchTerm || Object.keys(tableFilters).length > 0)) {
                toast.info('No tuition details found matching your criteria.');
            }
        } catch (error) {
            console.error("Error fetching tuition details:", error);
            toast.error(`Failed to fetch tuition details: ${error.message}`);
        } finally {
            setLoading(false);
        }
    }, [pagination.current, pagination.pageSize, searchTerm, tableFilters, sorter]);

    useEffect(() => {
        const handler = setTimeout(() => {
            fetchTuitionDetails();
        }, 500);
        return () => clearTimeout(handler);
    }, [fetchTuitionDetails]);

    const handleArchiveToggle = async (record) => {
        setLoading(true);
        try {
            // *** IMPORTANT: Updated API endpoint for archiving ***
            const response = await fetch(`${API_URL}/api/admin/tuition-details/archive/${record._id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ archive: !record.isArchived }),
            });
            const result = await response.json();
            if (!response.ok) {
                throw new Error(result.message || 'Failed to update archive status.');
            }
            toast.success(result.message);
            fetchTuitionDetails(); // Refresh data
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleTableChange = (newPagination, filters, newSorter, extra) => {
        if (['paginate', 'sort', 'filter'].includes(extra.action)) {
            const pageToFetch = (extra.action === 'sort' || extra.action === 'filter') ? 1 : newPagination.current;
            setTableFilters(filters);
            setSorter(newSorter.field ? { field: newSorter.field, order: newSorter.order, columnKey: newSorter.columnKey } : {});
            setPagination({ ...newPagination, current: pageToFetch });
        }
    };

    const handleSearchChange = (e) => setSearchTerm(e.target.value);
    const handleSearchSubmit = () => setPagination(prev => ({ ...prev, current: 1 }));
    const handleClearFiltersAndSearch = () => {
        setTableFilters({});
        setSorter({});
        setSearchTerm('');
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleRefresh = () => fetchTuitionDetails();
    
    // Updated navigation routes
    const handleCreateTuition = () => navigate('/admin/manage-tuition-details/create');
    const handleEditTuition = (record) => navigate(`/admin/manage-tuition-details/edit/${record._id}`);
    const handleViewTuition = (record) => navigate(`/admin/manage-tuition-details/edit/${record._id}?view=true`);

    // The columns array is completely new, based on TuitionDetails schema
    const columns = [
        { title: 'Academic Year', dataIndex: 'academicYear', key: 'academicYear', width: 150, sorter: true, sortOrder: sorter.columnKey === 'academicYear' ? sorter.order : null, },
        { title: 'Category', dataIndex: 'category', key: 'category', width: 220, filters: categoryFilterOptions, filteredValue: tableFilters.category || null, render: (text) => <Tooltip title={text}>{text}</Tooltip> },
        { title: 'Semester', dataIndex: 'semester', key: 'semester', width: 150, filters: semesterFilterOptions, filteredValue: tableFilters.semester || null },
        { title: 'Payment Type', dataIndex: 'paymentType', key: 'paymentType', width: 150, filters: paymentTypeFilterOptions, filteredValue: tableFilters.paymentType || null },
        { title: 'Tuition Fee', dataIndex: ['fees', 'tuition', 'finalAmount'], key: 'tuition', width: 130, align: 'right', render: formatCurrency },
        { title: 'Misc. Fees', dataIndex: ['fees', 'miscellaneous', 'finalTotal'], key: 'miscellaneous', width: 130, align: 'right', render: formatCurrency },
        { title: 'Other Fees', dataIndex: ['fees', 'otherFees', 'finalTotal'], key: 'otherFees', width: 130, align: 'right', render: formatCurrency },
        { title: 'Discount', dataIndex: ['fees', 'discount'], key: 'discount', width: 120, align: 'right', render: (text) => (text > 0 ? formatCurrency(text) : 'N/A') },
        { title: 'Total Amount', dataIndex: ['fees', 'finalTotal'], key: 'totalAmount', width: 150, align: 'right', render: (text) => <strong>{formatCurrency(text)}</strong>, sorter: true, sortOrder: sorter.columnKey === 'totalAmount' ? sorter.order : null, },
        { 
            title: 'Installment Plan', 
            key: 'installment', 
            width: 250,
            render: (_, record) => {
                if (record.paymentType === 'Installment' && record.fees?.installment?.breakdown?.length) {
                    const breakdown = record.fees.installment.breakdown.map(item => `${item.type.slice(0,1)}P: ${formatCurrency(item.amount)}`).join(' | ');
                    return <Tooltip title={breakdown}>{breakdown}</Tooltip>;
                }
                return 'N/A';
            } 
        },
        { title: 'Notes', dataIndex: 'notes', key: 'notes', ellipsis: true, width: 200, render: (text) => <Tooltip title={text}>{text}</Tooltip> },
        {
            title: 'Action', key: 'action', fixed: 'right', width: 180,
            render: (_, record) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                    <Button 
                        icon={record.isArchived ? <FaEye /> : <FaPen />} 
                        style={{ width: '150px', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }} 
                        onClick={() => record.isArchived ? handleViewTuition(record) : handleEditTuition(record)}
                    >
                        {record.isArchived ? 'View' : 'Edit'}
                    </Button>
                    <Button 
                        icon={record.isArchived ? <FaBoxOpen /> : <FaBoxArchive />} 
                        style={{ width: '150px', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }} 
                        onClick={() => handleArchiveToggle(record)} 
                        danger={!record.isArchived}
                    >
                        {record.isArchived ? 'Unarchive' : 'Archive'}
                    </Button>
                </div>
            )
        }
    ];

    return (
        <div className={`admin-page-container ${isSideNavOpen ? 'admin-sidenav-open' : ''}`}>
            <Header onMenuClick={toggleSideNav} isSideNavOpen={isSideNavOpen} />
            <div className="admin-content-wrapper">
                <div className="admin-sidenav-container">
                    <SideNav isOpen={isSideNavOpen} onClose={toggleSideNav} isSmallScreen={isSmallScreen} />
                </div>
                <main className="admin-main-content-area">
                    <div className="page-title"><p className="heading">Manage Tuition Details</p></div>
                    <div className="table-functions">
                        <div className="left-tools">
                            <Button icon={<FiFilter />} onClick={handleClearFiltersAndSearch}>Clear</Button>
                            <Button icon={<HiOutlineRefresh />} onClick={handleRefresh} loading={loading}>Refresh</Button>
                            <Button icon={<BiExport />} onClick={() => {/* Update handleExport if needed */}}>Export</Button>
                        </div>
                        <div className="right-tools">
                            <Input
                                placeholder="Search by Year, Category, Semester..."
                                allowClear
                                style={{ width: 300 }}
                                value={searchTerm}
                                onChange={handleSearchChange}
                                onPressEnter={handleSearchSubmit}
                                suffix={<FaSearch style={{ color: '#aaa', cursor: 'pointer' }} onClick={handleSearchSubmit} />}
                            />
                            {/* Updated Create Button */}
                            <Button type="primary" className="create-btn" icon={<FaPlus />} onClick={handleCreateTuition}>
                                Create Tuition
                            </Button>
                        </div>
                    </div>
                    <Spin spinning={loading}>
                        <Table
                            className="custom-table"
                            columns={columns}
                            dataSource={dataSource}
                            scroll={{ x: 2200 }}
                            pagination={pagination}
                            bordered
                            onChange={handleTableChange}
                            rowKey="_id"
                        />
                    </Spin>
                </main>
            </div>
            <Footer />
        </div>
    );
};

export default ManageTuitionDetails;